import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Box, Flex, VStack, useDisclosure } from '@chakra-ui/react';
import { isServer } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { BasicError } from '../../../../components/basic-error/BasicError';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { Field } from '../../../../components/field';
import { useLoginFields } from '../../../../components/forms/useLoginFields';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { useCustomer } from '../../../../contexts/useCustomer';
import { useLoginMutation } from '../../../../contexts/useLoginMutation';
import { useSearchParams } from '../../../../hooks';
import {
    RecaptchaWorkflowState,
    useRecaptchaWorkflow,
} from '../../../../recaptcha/hooks/useRecaptchaWorkflow';
import { RecaptchaAction, RecaptchaFeature } from '../../../../recaptcha/recaptcha';
import { useRecaptchaVerify } from '../../../../recaptcha/useRecaptchaVerify';
import { Button, Header, Hyperlink, Text } from '../../../../ui-components';
import { useUrl } from '../../../../utils/api';
import { apiErrorBody } from '../../../../utils/api-error-body';
import { PageUrl } from '../../../utils/page-url';
import { PasswordResetSuccessModal } from './password-reset-success-modal/PasswordResetSuccessModal';

const InternalLink: FC<ReactNode[]> = (chunks) => (
    <Hyperlink type="soft" to={PageUrl.TRACK_ORDER} data-testid="account-login-track-order">
        <Text>{chunks}</Text>
    </Hyperlink>
);

export interface UserLogin {
    email: string;
    password: string;
    rememberMe?: boolean;
}

interface LoginFormValues {
    email: string;
    password: string;
}

export interface LoginLocationState {
    email?: string;
    successfulPasswordReset?: boolean;
}

export const Login: FC = () => {
    const intl = useIntl();
    const form = useForm<LoginFormValues>();
    const fields = useLoginFields({ form, prefix: 'account-login' });
    const customer = useCustomer();
    const loginMutation = useLoginMutation();
    const recaptchaVerify = useRecaptchaVerify();
    const [error, setError] = useState<ReactNode>();
    const [lockedAccount, setLockedAccount] = useState(false);
    const location = useLocation<LoginLocationState>();
    const query = useSearchParams();
    const accountUrl = useUrl(PageUrl.ACCOUNT_SHOW);
    const { isOpen, onClose } = useDisclosure({
        defaultIsOpen: !!location.state?.successfulPasswordReset,
    });

    const {
        isRecaptchaEnabled,
        getRecaptchaPayload,
        handleApiError,
        handleApiSuccess,
        V2Widget,
        errorMessage: recaptchaError,
        isBlocked,
        state,
        resetWorkflow,
    } = useRecaptchaWorkflow({
        feature: RecaptchaFeature.ACCOUNT_LOGIN,
        action: RecaptchaAction.LOGIN,
        autoSubmitOnV2: true,
        onV2Complete: async () => {
            await onSubmit(form.getValues());
        },
    });

    const defaultEmail = location.state?.email !== undefined ? location.state.email : '';

    const onSubmit = async (userLogin: UserLogin): Promise<void> => {
        setLockedAccount(false);
        setError('');

        try {
            if (isRecaptchaEnabled) {
                const recaptchaPayload = await getRecaptchaPayload();
                if (!recaptchaPayload) {
                    throw new Error(`Failed to get reCAPTCHA payload for ${RecaptchaAction.LOGIN}`);
                }
                await recaptchaVerify.mutateAsync({
                    recaptcha: {
                        ...recaptchaPayload,
                        feature: RecaptchaFeature.ACCOUNT_LOGIN,
                    },
                });
            }

            await loginMutation.mutateAsync({ userLogin });

            handleApiSuccess();

            if (!isServer) {
                const redirectUrl = query.get('redirect-url');
                const url = redirectUrl ? decodeURIComponent(redirectUrl) : accountUrl;
                window.location.replace(url);
            }
        } catch (ex: unknown) {
            const wasRecaptchaError = handleApiError(ex);

            if (!wasRecaptchaError) {
                resetWorkflow();

                if (ex instanceof Error) {
                    const { message } = ex;
                    const errorBody = await apiErrorBody(ex);
                    const enrichedMessage = `${message ?? ''} --- ${errorBody.detail ?? ''}`;
                    let formattedError: ReactNode = message;
                    if (/invalid credentials/i.test(enrichedMessage)) {
                        formattedError = intl.formatMessage({
                            id: 'account.login.invalid.credentials',
                            defaultMessage:
                                "We couldn't recognize your email or password. Please try again.",
                        });
                    } else if (/account locked/i.test(enrichedMessage)) {
                        formattedError = intl.formatMessage({
                            id: 'account.login.account_locked',
                            defaultMessage:
                                "For your security we've locked your account. To unlock your account reset your password",
                        });
                        setLockedAccount(true);
                    }

                    setError(formattedError);
                }
            }
        }
    };

    return (
        <Box
            width={{ base: '100%', sm: '448px', xl: '640px' }}
            margin={{ base: '2rem 1rem', md: '0' }}
        >
            <VStack spacing={6} width="100%" data-testid="account-login-form-container">
                <VStack spacing={1} width="100%" alignItems="flex-start">
                    <Header as="h3" variant="header3" color="slt-pepper.1000" style="bold">
                        <FormattedMessage defaultMessage="Welcome Back!" id="account.login.title" />
                    </Header>
                    <Text color="slt-pepper.1000">
                        <FormattedMessage
                            defaultMessage="Sign in for a personalized cooking experience"
                            id="account.login.subtitle"
                        />
                    </Text>
                </VStack>

                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="w-full"
                    noValidate
                    data-testid="account-login-form"
                >
                    <VStack spacing={6} width="100%" alignItems="stretch">
                        {error || lockedAccount || recaptchaError ? (
                            <BasicError
                                options={{
                                    alertDataTestId: 'account-login-validation-error-container',
                                    errorMessageDataTestId:
                                        'account-login-validation-error-message',
                                    showOutline: false,
                                    iconVerticalAlign: 'top',
                                }}
                            >
                                {lockedAccount && (
                                    <Text color="slt-red-error">
                                        {error}{' '}
                                        <SoftLink
                                            to={{
                                                pathname: PageUrl.ACCOUNT_FORGOT_PASSWORD,
                                                state: {
                                                    email: form.getValues('email'),
                                                },
                                            }}
                                            className="underline"
                                            data-testid="account-login-locked-account-link"
                                        >
                                            <FormattedMessage
                                                defaultMessage="here"
                                                id="account.login.account_locked_here"
                                            />
                                        </SoftLink>
                                        .
                                    </Text>
                                )}
                                {!lockedAccount && recaptchaError && (
                                    <Text color="slt-red-error">{recaptchaError}</Text>
                                )}
                                {!lockedAccount && !recaptchaError && error && (
                                    <Text color="slt-red-error">{error}</Text>
                                )}
                            </BasicError>
                        ) : null}

                        <Field
                            {...fields.email}
                            defaultValue={defaultEmail}
                            variant="new-style"
                            label={<Text style="bold">Email Address</Text>}
                        />
                        <Field
                            {...fields.password}
                            variant="new-style"
                            label={<Text style="bold">Password</Text>}
                        />
                        <Flex justifyContent="end">
                            <Button variant="tertiary" padding="0">
                                <SoftLink
                                    to={{
                                        pathname: PageUrl.ACCOUNT_FORGOT_PASSWORD,
                                        state: {
                                            email: form.getValues('email'),
                                        },
                                    }}
                                    data-testid="account-login-forgot-password-link"
                                >
                                    <Text style="bold">
                                        <FormattedMessage
                                            defaultMessage="Forgot your password?"
                                            id="account.login.forgot_password"
                                        />
                                    </Text>
                                </SoftLink>
                            </Button>
                        </Flex>
                        <VStack spacing={2} width="100%">
                            <ConditionalRender
                                visibility={state === RecaptchaWorkflowState.V2_REQUIRED}
                            >
                                {V2Widget}
                            </ConditionalRender>
                            <Button
                                kind="submit"
                                id="account-login-sign-in"
                                variant="primary-black"
                                isDisabled={
                                    customer.isRegistered ||
                                    loginMutation.isLoading ||
                                    isBlocked ||
                                    recaptchaVerify.isLoading
                                }
                                wide
                                isLoading={loginMutation.isLoading || recaptchaVerify.isLoading}
                            >
                                <FormattedMessage
                                    id="account.login.sign_in"
                                    defaultMessage="SIGN IN"
                                />
                            </Button>

                            <Text
                                color="slt-pepper.1000"
                                justifyContent="center"
                                display="flex"
                                flexWrap="wrap"
                                gap={1}
                            >
                                <FormattedMessage
                                    defaultMessage="Don't have an account? <link>Track your order status here</link>"
                                    id="account.login.no_account"
                                    values={{
                                        link: InternalLink,
                                    }}
                                />
                            </Text>
                        </VStack>
                    </VStack>
                </form>
            </VStack>
            <PasswordResetSuccessModal isOpen={isOpen} onClose={onClose} />
        </Box>
    );
};
